import React, { useMemo, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Chip, Empty, Btn, Modal, Labelled, Notice } from "./ui/index.jsx";
import { takeBaseline } from "../lib/schedule/baseline.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { fmtDate, today } from "../lib/format.js";
import { workingDaysBetween } from "../lib/schedule/calendar.js";
import { buildWorkbook } from "../lib/exporters/xlsx.js";
import { scheduleSheets } from "../lib/exporters/schedule.js";
import { download, exportName, XLSX_TYPE } from "../lib/exporters/download.js";

/* Where the program stands, in one line each.
 *
 * A date on its own answers less than it looks. "SAT Complete, 29 March 2028"
 * is a fact nobody can act on. The same date next to how far it has moved is
 * the sentence that starts a conversation, which is why the movement column
 * appears as soon as there is a baseline to measure against.
 *
 * Milestones rather than phases, because a phase is a span and a milestone is
 * a commitment, and it is the commitments people ask about.
 */

const SHOWN = 5;

function movement(days) {
  if (days === null || days === undefined) return null;
  if (days === 0) return <span style={{ color: T.soft }}>on plan</span>;
  const late = days > 0;
  return (
    <span style={{ color: late ? "#8F2B25" : "#3E6B4F", fontWeight: 700, whiteSpace: "nowrap" }}>
      {late ? "+" : ""}{days}d {late ? "later" : "earlier"}
    </span>
  );
}

export default function KeyDates({ schedule, contract, ref_ = today() }) {
  const { canEdit, profile } = useAuth();
  const [freezing, setFreezing] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState(null);

  const rows = schedule?.computed?.activities ?? [];
  const baseline = schedule?.baselineRows ?? null;
  const baselineName = schedule?.baseline ?? null;

  /* The plan, as a file somebody else can open. Built in the browser from the
     rows already on screen, so what downloads and what is displayed cannot
     disagree, and nothing has to travel to a server and back. */
  const toExcel = () => {
    const sheets = scheduleSheets({
      activities: schedule?.computed?.activities ?? [],
      links: schedule?.links ?? [],
      baselineRows: schedule?.baselineRows ?? [],
    });
    download(buildWorkbook(sheets), exportName(contract, "schedule", "xlsx"), XLSX_TYPE);
  };

  const freeze = async () => {
    setBusy(true);
    const out = await takeBaseline({
      contractId: contract?.id,
      title: name.trim() || `Baseline ${fmtDate(today())}`,
      userId: profile?.id ?? null,
    });
    setBusy(false);
    if (!out.ok) { setSaid({ kind: "error", text: out.error }); return; }
    setFreezing(false);
    setName("");
    setSaid({ kind: "ok",
      text: `${out.baseline.title} saved, ${out.rows} activities frozen. From here, every date is measured against it.` });
    await schedule.reload();
  };

  const view = useMemo(() => {
    const was = new Map((baseline ?? []).map((b) => [b.activity_code, b]));

    const dated = rows
      .filter((r) => r.is_milestone && (r.early_finish || r.finish_date))
      .map((r) => {
        const when = r.early_finish ?? r.finish_date;
        const before = was.get(r.activity_code);
        let moved = null;
        if (before?.finish_date && when && before.finish_date !== when) {
          const early = before.finish_date < when ? before.finish_date : when;
          const late = before.finish_date < when ? when : before.finish_date;
          const d = workingDaysBetween(early, late) - 1;
          moved = when > before.finish_date ? d : -d;
        } else if (before?.finish_date) {
          moved = 0;
        }
        return {
          id: r.id, title: r.title, code: r.activity_code, when, moved,
          done: (r.percent_complete ?? 0) >= 100,
        };
      })
      .sort((a, b) => a.when.localeCompare(b.when));

    /* What is behind, then what is coming. Finished milestones are history and
       take up room that the next four need more. */
    const late = dated.filter((m) => !m.done && m.when < ref_);
    const ahead = dated.filter((m) => !m.done && m.when >= ref_);

    return {
      late,
      next: ahead.slice(0, Math.max(1, SHOWN - late.length)),
      finish: rows
        .filter((r) => !r.is_summary)
        .map((r) => r.early_finish ?? r.finish_date)
        .filter(Boolean)
        .sort()
        .at(-1),
      total: dated.length,
      done: dated.filter((m) => m.done).length,
    };
  }, [rows, baseline, ref_]);

  if (!schedule?.computed) return null;
  if (!view.total) {
    return (
      <Panel title="Key dates">
        <Empty text="No milestones in the schedule yet. Import a plan, or flag activities as milestones, and they appear here." />
      </Panel>
    );
  }

  const line = (m, kind) => (
    <div key={m.id} style={{
      display: "flex", gap: 10, alignItems: "baseline", padding: "7px 0",
      borderBottom: `1px solid ${T.line}`,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: 8, flexShrink: 0, alignSelf: "center",
        background: kind === "late" ? "#8F2B25" : kind === "soon" ? GOLD_INK : T.steel,
      }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: T.ink, overflowWrap: "break-word" }}>
        {m.title}
      </span>
      <span style={{ fontSize: 13, color: kind === "late" ? "#8F2B25" : T.navy,
                     fontWeight: 700, whiteSpace: "nowrap" }}>
        {fmtDate(m.when)}
      </span>
      <span style={{ fontSize: 12, width: 92, textAlign: "right" }}>
        {movement(m.moved)}
      </span>
    </div>
  );

  /* Inside thirty working days is close enough to plan around. */
  const soon = (when) => workingDaysBetween(ref_, when) <= 30;

  return (
    <Panel
      title="Key dates"
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {baselineName
            ? <Chip color={GOLD_INK}>{baselineName.title}</Chip>
            : <Chip color={T.soft}>No baseline yet</Chip>}
          <Chip color={T.steel}>{view.done} of {view.total} met</Chip>
          <Btn small kind="ghost" onClick={toExcel}>Export to Excel</Btn>
          {canEdit && (
            <Btn small kind={baselineName ? "ghost" : "gold"} onClick={() => setFreezing(true)}>
              {baselineName ? "Re-baseline" : "Take a baseline"}
            </Btn>
          )}
        </div>
      }
    >
      {freezing && (
        <Modal title={baselineName ? "Take a new baseline" : "Take a baseline"}
          onClose={() => setFreezing(false)}>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.75 }}>
              This freezes all {rows.length} activities exactly as they stand now.
              From then on every date is measured against it, and you see how far
              each one has moved rather than only where it is.
              {baselineName && (
                <>
                  <br /><br />
                  <b>{baselineName.title}</b> stops being the one things are measured
                  against. It is kept, not deleted, so the history of how this plan
                  has moved stays intact.
                </>
              )}
            </div>
            <Labelled label="Call it something"
              hint="What you would say in a meeting. The date is recorded either way.">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
                placeholder={`Rebaseline ${fmtDate(today())}`}
                style={{ width: "100%", padding: "7px 9px", border: `1px solid ${T.line}`,
                         borderRadius: 4, fontSize: 13.5, background: "#fff", color: T.ink }} />
            </Labelled>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn kind="ghost" onClick={() => setFreezing(false)}>Cancel</Btn>
              <Btn kind="gold" disabled={busy} onClick={freeze}>
                {busy ? "Freezing..." : `Freeze ${rows.length} activities`}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {said && (
        <div style={{ marginBottom: 10 }}>
          <Notice kind={said.kind}>{said.text}</Notice>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    paddingBottom: 9, borderBottom: `2px solid ${T.navy}`, gap: 10 }}>
        <span style={{ fontSize: 13, color: T.soft, textTransform: "uppercase",
                       letterSpacing: "0.06em", fontWeight: 700 }}>
          Program finishes
        </span>
        <span style={{ fontSize: 17, color: T.navy, fontWeight: 700, fontFamily: "inherit" }}>
          {fmtDate(view.finish)}
        </span>
      </div>

      {view.late.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                        textTransform: "uppercase", color: "#8F2B25", marginBottom: 3 }}>
            Behind
          </div>
          {view.late.map((m) => line(m, "late"))}
        </div>
      )}

      <div style={{ marginTop: view.late.length ? 12 : 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                      textTransform: "uppercase", color: GOLD_INK, marginBottom: 3 }}>
          Next up
        </div>
        {view.next.length === 0
          ? <Empty text="Every milestone in the plan has been met." />
          : view.next.map((m) => line(m, soon(m.when) ? "soon" : "ahead"))}
      </div>

      <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.7 }}>
        {baselineName
          ? `The last column is movement against ${baselineName.title}, in working days. Positive means later.`
          : "Take a baseline and the last column fills in with how far each one has moved from plan."}
      </div>
    </Panel>
  );
}
